import { Injectable, OnDestroy } from '@angular/core';

import {
  Action,
  Actions,
  NgxsOnInit,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  StateToken
} from '@ngxs/store';
import { Subject, takeUntil, tap } from 'rxjs';

import { ProductModelResponse } from '@app/core/models/api-response/product-response.model';
import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { UpdateDiscountTiersFromProductsAction } from '@app/features/cart-checkout/store/cart/cart.action';
import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';
import { RequestState } from '@app/shared/types/request-state';

import {
  GetProductsAction,
  GetProductsShowcaseAction
} from './products.action';
import { ProductModel } from './products.model';

export interface ProductsStateModel {
  products: ProductModel[];
  requestStatus: RequestState;
}

const PRODUCTS_STATE_TOKEN = new StateToken<ProductsStateModel>('products');

@State<ProductsStateModel>({
  name: PRODUCTS_STATE_TOKEN,
  defaults: {
    products: [],
    requestStatus: {
      errors: [],
      status: LoadingStateEnum.idle
    }
  }
})
@Injectable()
export class ProductsState implements NgxsOnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private http: MyWorldHttpService,
    private actions$: Actions
  ) {}

  ngxsOnInit(ctx: StateContext<ProductsStateModel>) {
    this.actions$
      .pipe(
        takeUntil(this.destroy$),
        ofActionSuccessful(GetProductsAction, GetProductsShowcaseAction),
        tap(() => {
          const products = ctx.getState().products;
          ctx.dispatch(new UpdateDiscountTiersFromProductsAction(products));
        })
      )
      .subscribe(() => {
        console.info(
          'Products loaded, updating discount tiers in cart state ✅'
        );
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Selector() static products({ products }: ProductsStateModel) {
    return products ?? [];
  }

  @Selector() static isLoading({ requestStatus }: ProductsStateModel) {
    return requestStatus.status === LoadingStateEnum.loading;
  }

  @Selector() static requestStatus({ requestStatus }: ProductsStateModel) {
    return requestStatus;
  }

  @Action(GetProductsAction) getProducts({
    patchState
  }: StateContext<ProductsStateModel>) {
    patchState({
      requestStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    return this.http.getProducts().pipe(
      catchRemoteError(
        (remoteError) => {
          patchState({
            products: [],
            requestStatus: {
              errors: [{ detail: remoteError.message }],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'Não foi possível carregar os produtos, tente novamente'
        }
      ),
      tap((products) => {
        patchState({
          products: this.#formatProducts(products),
          requestStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
      })
    );
  }

  @Action(GetProductsShowcaseAction) getProductsShowcase({
    patchState
  }: StateContext<ProductsStateModel>) {
    patchState({
      requestStatus: {
        errors: [],
        status: LoadingStateEnum.loading
      }
    });

    return this.http.getProductsShowcase().pipe(
      catchRemoteError(
        (remoteError) => {
          patchState({
            products: [],
            requestStatus: {
              errors: [{ detail: remoteError.message }],
              status: LoadingStateEnum.error
            }
          });
        },
        {
          defaultMessage:
            'Não foi possível carregar os produtos, tente novamente'
        }
      ),
      tap((products) => {
        patchState({
          products: this.#formatProducts(products),
          requestStatus: {
            errors: [],
            status: LoadingStateEnum.loaded
          }
        });
      })
    );
  }

  #formatProducts(products: ProductModelResponse[]): ProductModel[] {
    if (!products.length) {
      return [];
    }
    return products.map((product) => {
      const isKit = product.quantity >= 2;

      return {
        color: product.color,
        description: product.description,
        id: product.id,
        productId: product.productId,
        isKit,
        kitSize: isKit ? product.quantity : undefined,
        unitPrice: product.finalPrice,
        originalUnitPrice: product.originalUnitPrice,
        coverType: product.type,
        imageHash: product.imageHash,
        name: product.name,
        quantity: product.quantity,
        totalPrice: product.totalPrice,
        finalPrice: product.finalPrice,
        discountPercentage: product.discountPercentage,
        totalItemDiscount: product.totalItemDiscount
      };
    });
  }
}
